// One owner for browser marketing tags. GTM must not install another copy.
export const MARKETING_PREFERENCES_EVENT = 'marketing-consent-preferences';
const CONSENT_KEY = '1lead.marketing-consent.v1';
const ATTRIBUTION_KEY = '1lead.ad-attribution.v1';
const PURCHASES_KEY = '1lead.tracked-purchases.v1';
const DAY = 86400000;
const CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'];
const safeId = value => typeof value === 'string' && /^[A-Za-z0-9_.-]{1,250}$/.test(value);
const config = {
  pixel: /^\d+$/.test(process.env.REACT_APP_META_PIXEL_ID || '') ? process.env.REACT_APP_META_PIXEL_ID : '',
  ga: /^G-[A-Z0-9]+$/.test(process.env.REACT_APP_GA4_MEASUREMENT_ID || '') ? process.env.REACT_APP_GA4_MEASUREMENT_ID : '',
  ads: /^AW-\d+$/.test(process.env.REACT_APP_GOOGLE_ADS_ID || '') ? process.env.REACT_APP_GOOGLE_ADS_ID : '',
  purchaseLabel: process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || '',
  signupLabel: process.env.REACT_APP_GOOGLE_ADS_SIGNUP_LABEL || '',
};
let initialized = false;
let currentPage = '';
let memoryConsent;
let attribution;
let purchases;

function read(key, fallback) {
  try { return JSON.parse(window.localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
function write(key, value) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* Private browsing keeps an in-memory fallback. */ }
}
function safely(callback) { try { return callback(); } catch { return undefined; } }

export function marketingTrackingEnabled() {
  return typeof window !== 'undefined' && process.env.REACT_APP_MARKETING_TRACKING_ENABLED !== 'false'
    && (process.env.NODE_ENV === 'production' || process.env.REACT_APP_MARKETING_TRACKING_ENABLED === 'true')
    && Boolean(config.pixel || config.ga || config.ads);
}

export function getMarketingConsent() {
  const stored = read(CONSENT_KEY, memoryConsent);
  if (!stored || typeof stored.granted !== 'boolean' || !Number.isFinite(stored.at)
    || Date.now() - stored.at > 180 * DAY) return null;
  return stored.granted;
}

export function getMarketingConsentTimestamp() {
  const stored = read(CONSENT_KEY, memoryConsent);
  return Number.isFinite(stored?.at) ? stored.at : 0;
}

function googleConsent(granted) {
  const status = granted ? 'granted' : 'denied';
  return {ad_storage: status, ad_user_data: status, ad_personalization: status, analytics_storage: status};
}

export function setMarketingConsent(granted) {
  memoryConsent = {granted: granted === true, at: Date.now()};
  write(CONSENT_KEY, memoryConsent);
  if (!granted) {
    currentPage = '';
    attribution = {};
    write(ATTRIBUTION_KEY, {});
    safely(() => window.gtag?.('consent', 'update', googleConsent(false)));
    safely(() => window.fbq?.('consent', 'revoke'));
  } else {
    initializeMarketingTracking();
    safely(() => window.gtag?.('consent', 'update', googleConsent(true)));
    safely(() => window.fbq?.('consent', 'grant'));
  }
  window.dispatchEvent(new Event('marketing-consent-changed'));
}

function addScript(id, src) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id; script.async = true; script.src = src;
  document.head.appendChild(script);
}

export function initializeMarketingTracking() {
  if (!marketingTrackingEnabled()) return;
  if (getMarketingConsent() !== true) {
    safely(() => window.gtag?.('consent', 'update', googleConsent(false)));
    safely(() => window.fbq?.('consent', 'revoke'));
    return;
  }
  captureAttribution();
  if (initialized) return;
  initialized = true;
  if (config.ga || config.ads) safely(() => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('consent', 'default', googleConsent(false));
    window.gtag('consent', 'update', googleConsent(true));
    window.gtag('js', new Date());
    [config.ga, config.ads].filter(Boolean).forEach(id => window.gtag('config', id, {
      send_page_view: false, page_location: safePageUrl(), page_referrer: safeReferrer(),
    }));
    addScript('1lead-google-tag', `https://www.googletagmanager.com/gtag/js?id=${config.ga || config.ads}`);
  });
  if (config.pixel) safely(() => {
    if (!window.fbq) {
      const fbq = function fbq() { if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments); else fbq.queue.push(arguments); };
      fbq.queue = []; fbq.push = fbq; fbq.loaded = true; fbq.version = '2.0';
      window.fbq = fbq; window._fbq = fbq;
    }
    window.fbq('set', 'autoConfig', false, config.pixel);
    window.fbq('init', config.pixel);
    addScript('1lead-meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');
  });
}

function safeReferrer() {
  try { return `${new URL(document.referrer).origin}/`; } catch { return ''; }
}

export function safePageUrl() {
  const url = new URL(window.location.href);
  const filtered = new URLSearchParams();
  CLICK_KEYS.forEach(key => {if (safeId(url.searchParams.get(key))) filtered.set(key, url.searchParams.get(key));});
  ['utm_id', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
    const value = url.searchParams.get(key);
    if (/^[A-Za-z0-9_ -]{1,100}$/.test(value || '')) filtered.set(key, value);
  });
  return `${url.origin}${url.pathname}${filtered.size ? `?${filtered}` : ''}`;
}

function captureAttribution() {
  if (getMarketingConsent() !== true) return {};
  attribution = attribution || read(ATTRIBUTION_KEY, {});
  if (!Number.isFinite(attribution.at) || Date.now() - attribution.at > 90 * DAY) attribution = {};
  const params = new URLSearchParams(window.location.search);
  const clicks = {};
  CLICK_KEYS.forEach(key => {if (safeId(params.get(key))) clicks[key] = params.get(key);});
  if (Object.keys(clicks).some(key => clicks[key] !== attribution[key])) attribution = {...clicks, at: Date.now()};
  write(ATTRIBUTION_KEY, attribution);
  return attribution;
}

function cookie(name) {
  const entry = document.cookie.split('; ').find(part => part.startsWith(`${name}=`));
  return safely(() => decodeURIComponent(entry?.slice(name.length + 1) || '')) || '';
}

export function getCheckoutTracking() {
  const consent = marketingTrackingEnabled() && getMarketingConsent() === true;
  if (!consent) return {adsConsent: false, analyticsConsent: false};
  const saved = captureAttribution();
  const result = {adsConsent: true, analyticsConsent: true};
  ['gclid', 'gbraid', 'wbraid'].forEach(key => {if (safeId(saved[key])) result[key] = saved[key];});
  const fbc = saved.fbclid ? `fb.1.${saved.at}.${saved.fbclid}` : cookie('_fbc');
  const fbp = cookie('_fbp');
  if (safeId(fbc)) result.fbc = fbc;
  if (safeId(fbp)) result.fbp = fbp;
  const clientId = cookie('_ga').match(/^GA\d+\.\d+\.(\d+\.\d+)$/)?.[1];
  const gaSession = config.ga ? cookie(`_ga_${config.ga.slice(2)}`) : '';
  const sessionId = gaSession.match(/^GS1\.\d+\.(\d+)\./)?.[1] || gaSession.match(/(?:^|[.$])s(\d+)/)?.[1];
  if (clientId) result.gaClientId = clientId;
  if (sessionId) result.gaSessionId = sessionId;
  return result;
}

export function trackAnalyticsEvent(name, properties) {
  if (!marketingTrackingEnabled() || getMarketingConsent() !== true || !config.ga) return;
  safely(() => window.gtag?.('event', name, {...properties, send_to: config.ga,
    page_location: safePageUrl(), page_referrer: safeReferrer()}));
}

export function trackMarketingPage() {
  initializeMarketingTracking();
  if (!marketingTrackingEnabled() || getMarketingConsent() !== true) return;
  const page = window.location.pathname;
  if (currentPage === page) return;
  currentPage = page;
  trackAnalyticsEvent('page_view', {page_location: safePageUrl()});
  if (config.pixel) safely(() => window.fbq?.('trackSingle', config.pixel, 'PageView'));
}

export function trackMarketingEvent(name, properties = {}) {
  if (!marketingTrackingEnabled() || getMarketingConsent() !== true) return;
  initializeMarketingTracking();
  const meta = {view_item: 'ViewContent', begin_checkout: 'InitiateCheckout', sign_up: 'CompleteRegistration'}[name];
  if (!meta) return;
  const payload = {};
  if (/^[A-Z]{3}$/.test(properties.currency || '') && Number.isFinite(properties.value) && properties.value >= 0) {
    payload.currency = properties.currency; payload.value = properties.value;
  }
  if (Array.isArray(properties.items)) payload.items = properties.items.slice(0, 20).filter(item => safeId(item.item_id))
    .map(item => ({item_id: item.item_id, quantity: 1}));
  if (name === 'sign_up') payload.method = properties.method === 'google' ? 'google' : 'email';
  trackAnalyticsEvent(name, payload);
  if (config.pixel) safely(() => window.fbq?.('trackSingle', config.pixel, meta, {
    ...(payload.currency ? {value: payload.value, currency: payload.currency} : {}),
    ...(payload.items ? {content_ids: payload.items.map(item => item.item_id), content_type: 'product'} : {}),
  }));
  if (name === 'sign_up' && config.ads && safeId(config.signupLabel)) safely(() => window.gtag?.('event', 'conversion', {
    send_to: `${config.ads}/${config.signupLabel}`,
  }));
}

export function trackPurchase(receipt) {
  if (!marketingTrackingEnabled() || getMarketingConsent() !== true || receipt?.status !== 'paid'
    || !/^stripe_(?:cs|in)_[A-Za-z0-9_]+$/.test(receipt.transactionId || '')
    || !Number.isFinite(receipt.value) || receipt.value <= 0 || !/^[A-Z]{3}$/.test(receipt.currency || '')) return false;
  const payload = {transaction_id: receipt.transactionId, value: receipt.value, currency: receipt.currency,
    purchase_kind: receipt.purchaseKind, items: receipt.items};
  purchases = purchases || read(PURCHASES_KEY, []);
  if (!Array.isArray(purchases)) purchases = [];
  let delivered = true;
  const once = (destination, send) => {
    const key = `${destination}:${receipt.transactionId}`;
    if (purchases.includes(key)) return;
    try {send(); purchases.push(key); purchases = purchases.slice(-300); write(PURCHASES_KEY, purchases);} catch { delivered = false; }
  };
  if (config.pixel && window.fbq) once(`meta:${config.pixel}`, () => window.fbq('trackSingle', config.pixel, 'Purchase', {
    value: receipt.value, currency: receipt.currency, content_type: 'product', content_ids: [receipt.purchaseKind || 'payment'],
  }, {eventID: receipt.transactionId}));
  if (config.ga && window.gtag) once(`ga:${config.ga}`, () => window.gtag('event', 'purchase', {
    ...payload, value: receipt.analyticsValue ?? receipt.value, tax: receipt.tax, shipping: receipt.shipping,
    send_to: config.ga, page_location: safePageUrl(), page_referrer: safeReferrer(),
  }));
  if (config.ads && safeId(config.purchaseLabel) && window.gtag) once(`ads:${config.ads}/${config.purchaseLabel}`, () =>
    window.gtag('event', 'conversion', {...payload, send_to: `${config.ads}/${config.purchaseLabel}`,
      page_location: safePageUrl(), page_referrer: safeReferrer()}));
  return delivered;
}
